import { useEffect } from 'react';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { setLenis } from '@/lib/smoothScroll';

gsap.registerPlugin(ScrollTrigger);

/**
 * Desktop-only smooth wheel scrolling.
 * - Touch devices keep native scrolling (syncTouch off, and not created on coarse pointers).
 * - Disabled when the user prefers reduced motion.
 * - Driven by the GSAP ticker so ScrollTrigger and Lenis share one frame loop.
 * - Uses real window scrolling, so native position:sticky and scroll listeners keep working.
 */
export default function SmoothScroll() {
  useEffect(() => {
    const desktop = window.matchMedia('(hover: hover) and (pointer: fine)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let lenis: Lenis | null = null;
    let tick: ((time: number) => void) | null = null;

    const destroy = () => {
      if (tick) gsap.ticker.remove(tick);
      tick = null;
      lenis?.destroy();
      lenis = null;
      setLenis(null);
    };

    const create = () => {
      if (lenis || !desktop.matches || reduced.matches) return;
      lenis = new Lenis({
        lerp: 0.1,
        wheelMultiplier: 1,
        smoothWheel: true,
        syncTouch: false,
        autoRaf: false,
      });
      lenis.on('scroll', ScrollTrigger.update);
      tick = (time: number) => lenis?.raf(time * 1000);
      gsap.ticker.add(tick);
      gsap.ticker.lagSmoothing(0);
      setLenis(lenis);
      ScrollTrigger.refresh();
    };

    const sync = () => {
      if (desktop.matches && !reduced.matches) create();
      else destroy();
    };

    sync();
    desktop.addEventListener('change', sync);
    reduced.addEventListener('change', sync);
    return () => {
      desktop.removeEventListener('change', sync);
      reduced.removeEventListener('change', sync);
      destroy();
    };
  }, []);

  return null;
}